import React, { useMemo, useRef } from 'react'
import _ from 'lodash'
import utils from '../../utils'
import { mascaras } from '../../constants'
import { derivarMascara, parseMesAno, primeiroDiaDoMes } from './lts-calendar-utils'
import { Calendar } from 'primereact/calendar'

export default function LtsCalendar(allProps) {
  // `onChangeEvento` recebe o evento do Calendar inteiro (`e.value`, `e.target.name`), para as telas cujo handler
  // espera o evento; `onChange` recebe so o valor. Nao pode descer para o Calendar, que o repassaria ao DOM
  const { onChangeEvento, ...props } = allProps
  // houve digitacao que o Calendar nao reconheceu como data e que ainda nao foi repassada a tela
  const digitacaoInvalida = useRef(false)
  const chaveAnterior = useRef()
  // campo desabilitado e remontado sem passar pelo blur; a digitacao pendente dele nao vale mais
  if (props.disabled) {
    digitacaoInvalida.current = false
  }

  // todo campo de data sai com mascara. A tela pode passar a sua em `mask`, ou `mask={false}` para ficar sem
  const resolveMask = () => {
    if (props.mask !== undefined) {
      return props.mask || undefined
    }
    // sem campo de texto, ou com texto num formato que a mascara derivada nao cobre
    if (props.inline || props.timeOnly || props.showSeconds || props.hourFormat === '12') {
      return undefined
    }
    if (props.selectionMode && props.selectionMode !== 'single') {
      return undefined
    }
    return derivarMascara(props.dateFormat, props.showTime)
  }
  const mask = resolveMask()
  const semDia = props.dateFormat === mascaras.MASCARA_DATA_MM_YY && props.view !== 'month' && !props.showTime

  const repassar = (event) => {
    if (onChangeEvento) {
      onChangeEvento(event)
      return
    }
    props.onChange && props.onChange(event.target.value)
  }

  const onChange = (event) => {
    const target = event.target
    // com mascara, o Calendar avisa "sem data" a cada tecla enquanto a data esta incompleta. Repassar isso troca o
    // valor do campo e o Calendar reescreve o texto, apagando o que acabou de ser digitado. So vale como "sem data"
    // quando o campo ficou sem nenhum digito
    if (mask && !target.value && /\d/.test(_.get(event, 'originalEvent.target.value', ''))) {
      digitacaoInvalida.current = true
      return
    }
    digitacaoInvalida.current = false
    const data = props.view === 'month' ? primeiroDiaDoMes(target.value) : target.value
    if (data !== target.value) {
      repassar({ ...event, value: data, target: { ...target, value: data } })
      return
    }
    repassar(event)
  }

  // saiu do campo com data incompleta ou inexistente (31/02): o campo fica vazio, em vez de voltar calado para a
  // data anterior
  const onBlur = (event) => {
    if (digitacaoInvalida.current) {
      digitacaoInvalida.current = false
      repassar({
        originalEvent: event,
        value: null,
        stopPropagation: () => {},
        preventDefault: () => {},
        target: { name: resolveName(), id: props.id, value: null },
      })
    }
    props.onBlur && props.onBlur(event)
  }

  const resolveName = () => {
    return props.name || 'data'
  }

  const resolveLabel = () => {
    if (props.label) {
      return <label>{props.label || 'Data'}</label>
    }
    return null
  }

  // as telas que usam `onChangeEvento` vieram do Calendar direto e guardam a data no fuso do navegador; o dia
  // mostrado tem de ser o local. `getDateFromNumber` corta o dia em UTC e, das 21h em diante, mostraria o dia seguinte
  const dataLocal = !!onChangeEvento && !props.showTime && utils.isDate(props.value)
  const resolveChaveDoValor = () => {
    if (dataLocal) {
      return new Date(props.value.getFullYear(), props.value.getMonth(), props.value.getDate()).getTime()
    }
    return utils.isDate(props.value) ? props.value.getTime() : props.value
  }
  // a mesma data entre renderizacoes tem de ser o mesmo objeto: o Calendar reescreve o texto do campo sempre que
  // recebe um objeto novo, mesmo com a mesma data, e isso apagaria uma digitacao em andamento
  const chaveDoValor = resolveChaveDoValor()
  // o valor mudou por fora (carga do registro, lookup) com digitacao pendente: vale o valor novo, e sair do campo
  // nao pode apaga-lo
  if (!Object.is(chaveAnterior.current, chaveDoValor)) {
    chaveAnterior.current = chaveDoValor
    digitacaoInvalida.current = false
  }
  const value = useMemo(() => {
    if (dataLocal) {
      return new Date(chaveDoValor)
    }
    return props.showTime ? utils.getDateTimeFromNumber(props.value) : utils.getDateFromNumber(props.value)
  }, [chaveDoValor, props.showTime, dataLocal]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <React.Fragment>
      <span className="p-float-label">
        <Calendar
          {...props}
          // o Calendar so liga a mascara na montagem, e somente leitura se o campo nasce desabilitado; remontar
          // quando o campo e habilitado faz a mascara valer
          key={props.disabled ? 'desabilitado' : 'habilitado'}
          className={props.className}
          mask={mask}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          parseDateTime={props.parseDateTime || (semDia ? parseMesAno : undefined)}
          yearRange={props.yearRange || utils.getYearRangeFromDate('1800')}
          name={resolveName()}
          showButtonBar={true}
          view={props.view || 'date'}
          dateFormat={props.dateFormat}
          showTime={props.showTime}
        />
        {resolveLabel()}
      </span>
    </React.Fragment>
  )
}
